import { AxiosError } from "axios";
import { T_ApiResponse } from "../../../globalTypes/GlobalTypes";
import { T_CustomerLobDetailObj } from "../../Feature_Login/Login_Types";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux-store/CustomReduxHooks";
import { HiLobCode } from "../../../utils/constants";
import { useCallback, useEffect, useReducer } from "react";
import {
  useLazyFetchPolicyDetailsByMobileNumberQuery,
  useLazyFetchPolicyInsuredQuery,
} from "../HealthInsurance_Api";
import { isEmpty, uniqBy } from "lodash";
import { API_STATUS_CODE } from "../../../utils/ApiEndpoints";
import { normalizeHiInsuredPoliciesStatus } from "../../../utils/helper";
import {
  setHiAllPolicyDetails,
  setHiAllPolicyInsuredDetails,
  setHiSecondaryAllPolicyDetails,
} from "../HealthInsurance_Slice";
import { T_AppMobileNumberObj } from "../HealthInsurance_Types";
import {
  HI_HandlingReducerActions,
  HI_ReduxInitialState,
} from "./Hi_RedxActionHandler";
import { T_LocalHiBaseData } from "./Hi_BaseData_Types";

export const UseHiBaseDataLoader = (
  loadFor: "primary" | "secondary" = "primary",
  policyNumber: string = "",
  isHookCalled: boolean = true,
): T_LocalHiBaseData => {
  const [localState, updateLocalState] = useReducer(
    HI_HandlingReducerActions,
    HI_ReduxInitialState,
  );
  const dispatch = useAppDispatch();
  const userDetails = useAppSelector((state) => state.CsAppSlice.userDetails);
  const hiPrimaryAllPolicyDetails = useAppSelector(
    (state) => state.HealthInsuranceSlice.allPolicyDetails,
  );
  const hiSecondaryAllPolicyDetails = useAppSelector(
    (state) => state.HealthInsuranceSlice.secondaryAllPolicyDetails,
  );
  const policyInsuredDetails = useAppSelector(
    (st) => st.HealthInsuranceSlice.allPolicyInsuredDetails,
  );
  const customerAndLobDetails = userDetails?.customerAndLobDetails;
  const UserHiLobId =
    customerAndLobDetails?.filter(
      (item: T_CustomerLobDetailObj) =>
        item.LOB === HiLobCode && item.PRIMARY_SECONDARY_USER === loadFor,
    ) || [];
  const [
    fetchPolicyDetailsByMobileApi,
    {
      error: errInPolicyDetailsByMobile,
      isFetching: isLoadingPolicyDetailsByMobileApi,
    },
  ] = useLazyFetchPolicyDetailsByMobileNumberQuery();
  const [
    fetchPolicyInsuredApi,
    { error: errInPolicyInsuredApi, isFetching: isLoadingPolicyInsuredApi },
  ] = useLazyFetchPolicyInsuredQuery();

  const fetchAllHiPolicyDetailsByMobile = async () => {
    if (isLoadingPolicyDetailsByMobileApi) {
      return;
    }
    updateLocalState({ type: "fetching", payload: "" });
    const ApiListPromises = uniqBy(UserHiLobId, "MOBILE")?.map(
      (item: T_CustomerLobDetailObj) => {
        return fetchPolicyDetailsByMobileApi(item.MOBILE, true);
      },
    );
    if (loadFor === "primary") {
      /** Process Primary policies and save to redux */
      let primaryUserResults = await Promise.all(ApiListPromises);
      if (primaryUserResults.some((r) => r?.error || r?.data?.code != 1)) {
        return;
      }
      if (primaryUserResults.some((r) => isEmpty(r.data?.payload.Response))) {
        updateLocalState({ type: "error", payload: Error("Empty List") });
        return;
      }
      let transformedPrimaryUserResults: T_AppMobileNumberObj[] = [];
      primaryUserResults.forEach((result) => {
        if (
          result.data?.payload.Response &&
          !isEmpty(result?.data?.payload.Response)
        ) {
          transformedPrimaryUserResults.push(...result.data?.payload.Response);
        }
      });
      dispatch(setHiAllPolicyDetails(transformedPrimaryUserResults));
    } else {
      /** Process Secondary policies and save to redux */
      let secondaryUserResults = await Promise.all(ApiListPromises);
      if (secondaryUserResults.some((r) => r?.error || r?.data?.code != 1)) {
        updateLocalState({ type: "error", payload: Error("Api Failed!") });
        return;
      }
      let transformedSecondaryUserResults: T_AppMobileNumberObj[] = [];
      secondaryUserResults.forEach((result) => {
        if (
          result.data?.payload.Response &&
          !isEmpty(result?.data?.payload.Response)
        ) {
          let policiesArray = result.data?.payload.Response;
          let selectedPolicy = policiesArray.filter((item) => {
            let policyNumberArray = UserHiLobId.map(
              (i) => i.HOLDING_IDENTIFIER_ONE,
            );
            return policyNumberArray.includes(item.PolicyNumber);
          });
          if (selectedPolicy.length > 0) {
            transformedSecondaryUserResults.push(...selectedPolicy);
          }
        }
      });
      dispatch(setHiSecondaryAllPolicyDetails(transformedSecondaryUserResults));
    }
  };

  /** Load when policy number is passed in search param for given route,
   * DO: Load API result and save to redux
   */
  const policyWiseDetails = useCallback(() => {
    if (isLoadingPolicyInsuredApi) {
      return;
    }
    updateLocalState({ type: "fetching", payload: "" });
    fetchPolicyInsuredApi(policyNumber, true).then((res) => {
      if (res.data?.code === API_STATUS_CODE.SUCCESS) {
        if (isEmpty(res?.data?.payload?.Response)) {
          updateLocalState({ type: "error", payload: Error("Empty List") });
        } else {
          const transformedResultWithPolicyEndDateCheck =
            normalizeHiInsuredPoliciesStatus([res?.data?.payload?.Response]);
          dispatch(
            setHiAllPolicyInsuredDetails(
              transformedResultWithPolicyEndDateCheck,
            ),
          );
        }
      } else {
        updateLocalState({ type: "error", payload: Error("Empty List") });
      }
    });
  }, [policyNumber, isLoadingPolicyInsuredApi]);

  const checkAndInitHiApiFlow = useCallback(() => {
    if (isEmpty(UserHiLobId)) {
      updateLocalState({
        type: "error",
        payload: Error("Hi client id not present"),
      });
    } else if (
      (loadFor === "primary" && isEmpty(hiPrimaryAllPolicyDetails)) ||
      (loadFor === "secondary" && isEmpty(hiSecondaryAllPolicyDetails))
    ) {
      /** Loading HI Dashboard Details */
      fetchAllHiPolicyDetailsByMobile();
    } else if (
      !isEmpty(policyNumber) &&
      isEmpty(
        policyInsuredDetails?.filter(
          (f) => f?.PolicyDetail?.PolicyNumber === policyNumber,
        ),
      )
    ) {
      policyWiseDetails();
    } else {
      updateLocalState({ type: "success", payload: "" });
    }
  }, [
    policyNumber,
    UserHiLobId,
    hiPrimaryAllPolicyDetails,
    hiSecondaryAllPolicyDetails,
    policyInsuredDetails,
  ]);

  useEffect(() => {
    // if (!isEmpty(policyNumber)) {
    if (isHookCalled) {
      checkAndInitHiApiFlow();
    }
    // }
  }, [
    hiPrimaryAllPolicyDetails,
    policyNumber,
    policyInsuredDetails,
    isHookCalled,
  ]);

  useEffect(() => {
    if (errInPolicyDetailsByMobile || errInPolicyInsuredApi) {
      updateLocalState({ type: "error", payload: Error("Api Failed!") });
    }
  }, [errInPolicyDetailsByMobile, errInPolicyInsuredApi]);

  return localState;
};
